/**
 * Who did what — the per-person view of the same change log the grid draws.
 *
 * WHY THIS EXISTS
 *   The grid answers "when". This answers "who, and what kind of work". On a
 *   six-person team building an instrument, that is not a vanity leaderboard:
 *   it is how you know which colleague to ask about a drawing, and whether a
 *   week of "activity" was new work or a round of revisions.
 *
 * DATA DEFINITIONS
 *
 *   An Overview is { total, files, busiest, from, to }.
 *   Interpretation: the whole log in one line. `files` counts DISTINCT PATHS
 *   ever touched — a file added, revised four times and deleted is one file,
 *   not six — which is the number a person means when they ask "how many
 *   drawings are in here?".
 *
 *   A Segment is { key, colour, label, n }.
 *   Interpretation: one slice of a person's proportion bar. Slices carry the
 *   semantic change-kind tokens, not a decorative palette, so the same red
 *   means "deleted" here, in the file list, and in the activity feed.
 *
 * Charts are drawn with divs. A bar of four proportions and a column of
 * numbers do not justify a charting library, and the user asked us to keep
 * third-party dependencies few.
 *
 * THIS COMPONENT IS PURE PRESENTATION: props in, pixels out. No store, no
 * fetching. The clock is read once, to turn timestamps into "5 months ago".
 */
import * as React from 'react'
import {
  dayKeyOf, statsByAuthor, tallyByDay,
  type AuthorStats, type Change, type ContributionDay, type DayKey, type User, type UserId,
} from '@lattice/core'
import { cn } from '@/lib/utils'
import { pluralise } from '@/lib/format'
import { Avatar, EmptyState } from '@/components/ui/primitives'
import { Users } from 'lucide-react'

/* ------------------------------ Formatting ---------------------------- */

/** Fixed locale and UTC, for the same reasons as ContributionGrid. */
const DAY_FMT = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' })
const FULL_FMT = new Intl.DateTimeFormat('en-GB', {
  weekday: 'short', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC',
})
const RELATIVE = new Intl.RelativeTimeFormat('en-GB', { numeric: 'auto' })

const asDate = (day: DayKey): Date => new Date(`${day}T00:00:00Z`)

/** "12 Aug". formatShort : DayKey -> String */
const formatShort = (day: DayKey): string => DAY_FMT.format(asDate(day))

/**
 * The full team-calendar day an INSTANT fell on.
 *
 * The route through dayKeyOf is the whole point: an AuthorStats timestamp is a
 * UTC instant, and the team is at UTC+05:30, so a commit at 20:00 UTC belongs
 * to the NEXT day here. Formatting the raw instant in UTC put a date in the
 * hover text that contradicted the square the grid had drawn it in.
 * formatInstant : String -> String
 */
const formatInstant = (iso: string): string => FULL_FMT.format(asDate(dayKeyOf(iso)))

/** Descending, so the first unit that fits is the coarsest honest one. */
const UNITS: readonly (readonly [Intl.RelativeTimeFormatUnit, number])[] = [
  ['year', 31_536_000_000],
  ['month', 2_592_000_000],
  ['week', 604_800_000],
  ['day', 86_400_000],
  ['hour', 3_600_000],
  ['minute', 60_000],
]

/**
 * "5 months ago", "yesterday", "just now".
 *
 * `now` is a parameter rather than a call to Date.now() inside, so the function
 * is total and testable and the whole card agrees on one instant — otherwise
 * two rows rendered a millisecond apart could disagree about "today".
 * relativeTime : String Number -> String
 */
export function relativeTime(iso: string, now: number): string {
  const delta = Date.parse(iso) - now
  if (Number.isNaN(delta)) return 'unknown'
  for (const [unit, ms] of UNITS) {
    if (Math.abs(delta) >= ms) return RELATIVE.format(Math.round(delta / ms), unit)
  }
  return 'just now'
}

/* ------------------------------- Overview ----------------------------- */

interface Overview {
  readonly total: number
  readonly files: number
  readonly busiest?: ContributionDay
  readonly from?: DayKey
  readonly to?: DayKey
}

/**
 * Fold the log into its headline numbers.
 * One pass for the distinct paths and the span; tallyByDay does the rest.
 * overviewOf : [Change] -> Overview
 */
function overviewOf(changes: readonly Change[]): Overview {
  if (changes.length === 0) return { total: 0, files: 0 }

  const paths = new Set<string>()
  for (const c of changes) paths.add(c.path)

  const days = [...tallyByDay(changes).values()]
  // Ties break towards the LATER day, so the headline does not flicker between
  // two equally busy days depending on the order the log happened to arrive in.
  const busiest = days.reduce(
    (best, d) => (d.total > best.total || (d.total === best.total && d.date > best.date) ? d : best),
    days[0],
  )
  const keys = days.map(d => d.date).sort()

  return {
    total: changes.length,
    files: paths.size,
    busiest,
    from: keys[0],
    to: keys[keys.length - 1],
  }
}

/* ------------------------------- Segments ----------------------------- */

/**
 * The four kinds, in the order a revision history is read: things appeared,
 * then changed, then went away, then moved.
 */
const KINDS = [
  { key: 'added', colour: 'var(--added)', label: 'added' },
  { key: 'modified', colour: 'var(--modified)', label: 'modified' },
  { key: 'deleted', colour: 'var(--deleted)', label: 'deleted' },
  { key: 'renamed', colour: 'var(--renamed)', label: 'renamed' },
] as const

type KindKey = (typeof KINDS)[number]['key']

/** The non-empty slices of one person's work. segmentsOf : AuthorStats -> [Segment] */
const segmentsOf = (stats: AuthorStats) =>
  KINDS.map(k => ({ ...k, n: stats[k.key] })).filter(s => s.n > 0)

/**
 * One person's work as a 6px bar: LENGTH is their volume against the busiest
 * contributor, SEGMENTS are their mix of change kinds.
 *
 * Two encodings because each answers a different question and neither is worth
 * a second row of pixels. Normalising every bar to full width would make the
 * list's ranking invisible — the thing it is a ranked list of — while length
 * alone would hide that one person's 200 changes were revisions and another's
 * were new drawings.
 *
 * Two floors keep it honest at small sizes: 8% of the track, so a single change
 * is still a mark and not a rounding error, and 3px per non-empty slice, so a
 * category is never silently dropped (2 renames in 776 changes is 0.26% — a
 * truthful width would vanish). Exact counts live in the label and each slice's
 * title, which is where precision belongs.
 */
function ProportionBar({ stats, peak }: { stats: AuthorStats; peak: number }) {
  const segments = segmentsOf(stats)
  const label =
    `${pluralise(stats.total, 'change')}: ` +
    segments.map(s => `${s.n} ${s.label}`).join(', ')
  const width = peak > 0 ? Math.max(8, (stats.total / peak) * 100) : 0
  return (
    <div className="h-1.5 w-full rounded-full bg-[var(--bg-inset)]">
      <div
        role="img"
        aria-label={label}
        className="flex h-full overflow-hidden rounded-full"
        style={{ width: `${width}%` }}
      >
        {segments.map(s => (
          <div
            key={s.key}
            title={`${s.n} ${s.label}`}
            style={{ flexGrow: s.n, flexBasis: 0, minWidth: 3, background: s.colour }}
          />
        ))}
      </div>
    </div>
  )
}

/* --------------------------------- Parts ------------------------------ */

/**
 * One headline number with its caption.
 * `suffix` is shown quietly beside the value; `title` is only ever the hover
 * text. Keeping them separate is not pedantry — conflating them once put a
 * full date string inline next to the range it was meant to explain.
 */
function Stat({ label, value, suffix, title }: {
  label: string; value: string; suffix?: string; title?: string
}) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wide text-[var(--fg-subtle)]">{label}</div>
      <div className="truncate text-sm text-[var(--fg)] tabular" title={title ?? value}>
        {value}
        {suffix && <span className="ml-1 text-[var(--fg-subtle)]">{suffix}</span>}
      </div>
    </div>
  )
}

/** A middot that the screen reader skips. */
const Dot = () => <span aria-hidden className="text-[var(--fg-subtle)]">·</span>

/**
 * Stand in for an author who is not on the roster.
 *
 * Imported history can name someone who has since left, and a provenance view
 * that silently drops their work would be lying. Better an anonymous chip than
 * a missing row.
 * placeholderUser : UserId -> User
 */
const placeholderUser = (id: UserId): User => ({
  id,
  name: id,
  email: '',
  initials: id.slice(0, 2).toUpperCase(),
  accent: 0,
})

/* ------------------------------- Component ---------------------------- */

export interface ActivityStatsProps {
  readonly changes: readonly Change[]
  readonly roster: readonly User[]
  /** The person whose row is highlighted, when the caller filters by author. */
  readonly selectedAuthor?: UserId
  /**
   * Called when a contributor's row is activated. Supplying it turns each row
   * into a real button — focusable, Enter-activatable, and announced as a
   * toggle — rather than a div that happens to respond to clicks.
   */
  onSelectAuthor?: (authorId: UserId) => void
  readonly className?: string
}

/**
 * A ranked list of contributors, over a compact summary of the whole log.
 * ActivityStats : ActivityStatsProps -> Element
 */
export function ActivityStats({
  changes, roster, selectedAuthor, onSelectAuthor, className,
}: ActivityStatsProps) {
  // One instant for the whole card, read once per mount.
  const now = React.useMemo(() => Date.now(), [])

  const overview = React.useMemo(() => overviewOf(changes), [changes])
  const ranked = React.useMemo(() => statsByAuthor(changes), [changes])
  const people = React.useMemo(
    () => new Map(roster.map(u => [u.id, u])),
    [roster],
  )

  if (changes.length === 0) {
    return (
      <div className={className}>
        <EmptyState
          icon={Users}
          title="Nothing to summarise yet"
          hint="As soon as anyone adds or revises a file, their work shows up here."
        />
      </div>
    )
  }

  const totals = totalsByKind(ranked)
  // Bars are measured against the busiest contributor, so the leader's bar is
  // always full and everyone else is read as a fraction of it.
  const peak = ranked[0]?.total ?? 0

  return (
    <div className={cn('fade-in', className)}>
      <div className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4">
        <Stat label="Changes" value={overview.total.toLocaleString('en-GB')} />
        <Stat label="Files touched" value={overview.files.toLocaleString('en-GB')} />
        <Stat
          label="Busiest day"
          value={overview.busiest ? formatShort(overview.busiest.date) : '—'}
          suffix={overview.busiest ? pluralise(overview.busiest.total, 'change') : undefined}
          title={overview.busiest ? FULL_FMT.format(asDate(overview.busiest.date)) : undefined}
        />
        <Stat
          label="Span"
          value={
            overview.from && overview.to
              ? `${formatShort(overview.from)} – ${formatShort(overview.to)}`
              : '—'
          }
          title={
            overview.from && overview.to
              ? `${FULL_FMT.format(asDate(overview.from))} to ${FULL_FMT.format(asDate(overview.to))}`
              : undefined
          }
        />
      </div>

      {/* One legend for every bar below it. */}
      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-[var(--border)] pt-2.5 text-[10px] text-[var(--fg-subtle)]">
        {KINDS.filter(k => totals[k.key] > 0).map(k => (
          <span key={k.key} className="inline-flex items-center gap-1">
            <span
              aria-hidden
              className="inline-block h-1.5 w-1.5 rounded-full"
              style={{ background: k.colour }}
            />
            <span className="tabular">{totals[k.key].toLocaleString('en-GB')}</span> {k.label}
          </span>
        ))}
      </div>

      <ul className="mt-1" aria-label="Contributors, busiest first">
        {ranked.map(stats => {
          const person = people.get(stats.authorId) ?? placeholderUser(stats.authorId)
          const share = shareOf(stats.total, overview.total)
          const active = selectedAuthor === stats.authorId
          // A row is a button only when pressing it does something. Rendering a
          // button that ignores its own click is how keyboard users lose trust.
          const Row = onSelectAuthor ? 'button' : 'div'
          return (
            <li key={stats.authorId}>
              <Row
                {...(onSelectAuthor
                  ? {
                      type: 'button' as const,
                      onClick: () => onSelectAuthor(stats.authorId),
                      'aria-pressed': active,
                      title: active ? `Show everyone again` : `Show only ${person.name}`,
                    }
                  : {})}
                className={cn(
                  'snap flex w-full items-start gap-2.5 rounded-[var(--radius-md)] px-2 py-2 text-left',
                  onSelectAuthor && 'hover:bg-[var(--bg-hover)]',
                  active && 'bg-[var(--accent-soft)]',
                )}
              >
              <Avatar user={person} size={24} className="mt-0.5" />

              <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="truncate text-sm font-medium text-[var(--fg)]">{person.name}</span>
                  <span className="shrink-0 text-sm text-[var(--fg)] tabular">
                    {stats.total.toLocaleString('en-GB')}
                    <span className="ml-1 text-[10px] text-[var(--fg-subtle)]">{share}</span>
                  </span>
                </div>

                <div className="mt-1.5">
                  <ProportionBar stats={stats} peak={peak} />
                </div>

                <div className="mt-1.5 flex flex-wrap items-center gap-x-1.5 text-[10px] text-[var(--fg-subtle)]">
                  <span className="tabular">{stats.activeDays} active days</span>
                  <Dot />
                  <span className="tabular">{stats.longestStreak}-day streak</span>
                  <Dot />
                  <span title={`First: ${formatInstant(stats.firstAt)}`}>
                    {relativeTime(stats.firstAt, now)}
                  </span>
                  <span aria-hidden>→</span>
                  <span title={`Last: ${formatInstant(stats.lastAt)}`}>
                    {relativeTime(stats.lastAt, now)}
                  </span>
                </div>
              </div>
              </Row>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

/**
 * Totals per kind across everyone, for the legend.
 * totalsByKind : [AuthorStats] -> {added, modified, deleted, renamed}
 */
function totalsByKind(ranked: readonly AuthorStats[]): Record<KindKey, number> {
  const out: Record<KindKey, number> = { added: 0, modified: 0, deleted: 0, renamed: 0 }
  for (const s of ranked) for (const k of KINDS) out[k.key] += s[k.key]
  return out
}

/**
 * A person's share of the log, never rounded down to a flat "0%" — someone who
 * contributed is not nobody.
 * shareOf : Number Number -> String
 */
function shareOf(part: number, whole: number): string {
  if (whole === 0) return '0%'
  const pct = (part / whole) * 100
  return pct < 1 ? '<1%' : `${Math.round(pct)}%`
}
